import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Typography,
  Box,
  Link,
  IconButton,
  CircularProgress,
  Backdrop,
  Grid,
} from "@material-ui/core";
import { Slide } from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DescriptionIcon from "@material-ui/icons/Description";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch } from "react-redux";
import { showMessage } from "../../redux/store/actions/alerts";
import { DatePicker, Select } from "./mui-formik-inputs";
import { sortOrder } from "util/customers/deliveryTimings";
import moment from "moment";

const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Generic error message translator that handles common patterns
 * @param {string} errorMsg - The error message to translate
 * @param {Function} t - Translation function
 * @returns {string} Translated error message
 */
const translateErrorMessage = (errorMsg, t) => {
  if (!errorMsg) return "";

  const msg = String(errorMsg).replace(
    /\(lines:\s*/gi,
    `(${t("atbas_import_excel_row_label")}: `
  );

  try {
    const isColumnKey = /^[a-z0-9_]+$/i.test(errorMsg);
    if (isColumnKey) {
      const key = `import_error.${errorMsg}`;
      const translated = t(key);
      if (translated && translated !== key) return translated;
    }

    const existsPattern = /^(.+) "([^"]+)" at this address already exists$/;
    const existsMatch = msg.match(existsPattern);
    if (existsMatch) {
      const [_, entityType, entityName] = existsMatch;
      return t("{{entityType}} \"{{entityName}}\" at this address already exists", {
        entityType: t(entityType),
        entityName
      });
    }

    const notFoundPattern = /^(.+) "([^"]+)" not found$/;
    const notFoundMatch = msg.match(notFoundPattern);
    if (notFoundMatch) {
      const [_, entityType, entityName] = notFoundMatch;
      return t("{{entityType}} \"{{entityName}}\" not found", {
        entityType: t(entityType),
        entityName
      });
    }

    const nestedPattern = /^(.+) "([^"]+)" not found for (.+) "([^"]+)"$/;
    const nestedMatch = msg.match(nestedPattern);
    if (nestedMatch) {
      const [_, entity1Type, entity1Name, entity2Type, entity2Name] = nestedMatch;
      return t("{{entity1Type}} \"{{entity1Name}}\" not found for {{entity2Type}} \"{{entity2Name}}\"", {
        entity1Type: t(entity1Type),
        entity1Name,
        entity2Type: t(entity2Type),
        entity2Name
      });
    }

    const validationPattern = /^"([^"]+)" (.+)$/;
    const validationMatch = msg.match(validationPattern);
    if (validationMatch) {
      const [_, field, errorReason] = validationMatch;
      return t("\"{{field}}\" {{errorReason}}", {
        field: t(field),
        errorReason: t(errorReason)
      });
    }

    if (msg.includes("is required")) {
      return t(msg);
    }

    if (msg.includes("must be")) {
      return t(msg);
    }

    return t(msg);
  } catch (e) {
    console.error("Error parsing message for translation:", e);
    return t(msg);
  }
};

/**
 * Reusable file import modal for both orders and customers
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.t - Translation function
 * @param {Function} props.onImportComplete - Callback on successful import
 * @param {Function} props.onImport - Function to call for importing (receives file)
 * @param {string} props.title - Modal title
 * @param {string|React.ReactNode} props.description - Modal description
 * @param {string} props.templateUrl - URL for template download
 * @param {string} props.templateName - Name for template download
 * @param {Array} props.acceptedFileTypes - Array of accepted file types
 * @param {Object} props.importErrors - Error object from redux state
 * @param {string} props.errorMessaage - Error message in string
 */
const FileImportModal = ({
  open,
  onClose,
  t,
  onImportComplete,
  onImport,
  title = "Import Data",
  description = "Upload an Excel file (.xlsx or .xls) containing order data. The file should follow the required format",
  templateUrl = "/assets/templates/import_template.xlsx",
  templateName = "import_template",
  acceptedFileTypes = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".xlsx",
    ".xls",
  ],
  errorMessage,
  importErrors = [],
  multilineImportErrors = false,
  showDateDeparture = false,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = React.useRef(null);
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localErrors, setLocalErrors] = useState([]);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedDeparture, setSelectedDeparture] = useState("");

  useEffect(() => {
    if (!open) {
      setFile(null);
      setUploadProgress(0);
      setLocalErrors([]);
      setSelectedDate(moment());
      setSelectedDeparture("");
    } else {
      setSelectedDate(moment());
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (importErrors && Array.isArray(importErrors)) {
      setLocalErrors(importErrors);
    }
  }, [importErrors]);

  const validateAndSetFile = (selectedFile) => {
    const fileExtension = selectedFile?.name?.toLowerCase().split(".").pop();

    setLocalErrors([]);
    setUploadProgress(0);

    if (
      selectedFile &&
      (acceptedFileTypes.includes(selectedFile.type) ||
        acceptedFileTypes.includes(`.${fileExtension}`))
    ) {
      setFile(selectedFile);
    } else {
      dispatch(
        showMessage({
          message: t("Please select a valid file (.xls, .xlsx)"),
          type: "error",
        })
      );
      setFile(null);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const droppedFile = event.dataTransfer.files[0];
      validateAndSetFile(droppedFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setLocalErrors({});
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImport = async () => {
    if (!file) {
      dispatch(
        showMessage({
          message: t("Please select a file to import"),
          type: "warning",
        })
      );
      return;
    }

    if (
      showDateDeparture &&
      (!selectedDate || !selectedDeparture || (selectedDate && selectedDate.isBefore(moment(), "day")))
    ) {
      dispatch(
        showMessage({
          message: t("Please select both date and departure"),
          type: "warning",
        })
      );
      return;
    }

    setIsUploading(true);
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      const response = await onImport(
        file,
        showDateDeparture && selectedDate ? selectedDate.format("YYYY-MM-DD") : undefined,
        showDateDeparture ? selectedDeparture : undefined
      );

      clearInterval(progressInterval);

      if (response && response.success) {
        setUploadProgress(100);
        if (onImportComplete) {
          onImportComplete();
          onClose();
        }
        setTimeout(() => {
          setFile(null);
          setIsUploading(false);
        }, 1500);
      } else {
        setUploadProgress(0);
        setIsUploading(false);

        if (response && response.errors) {
          setLocalErrors(response.errors);
        }
      }
    } catch (error) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      setIsUploading(false);
      console.error("Import error:", error);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  const downloadTemplate = (e) => {
    e.preventDefault();
    const link = document.createElement("a");
    link.href = templateUrl;
    link.download = `${templateName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Backdrop
        open={open && isLoading}
        style={{
          zIndex: 1301,
          color: "#fff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={SlideTransition}
        transitionDuration={{ enter: 500, exit: 300 }}
        aria-labelledby="import-dialog-title"
      >
        <DialogTitle id="import-dialog-title">
          {t(title)}
          {!isUploading && (
            <IconButton
              aria-label="close"
              onClick={handleClose}
              style={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "grey",
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {typeof description === "string" ? t(description) : description}
          </DialogContentText>

          <Box mt={3} mb={2}>
            <Typography variant="subtitle2">
              <Link href="#" onClick={downloadTemplate}>
                {t("Download template")}
              </Link>
            </Typography>
          </Box>

          {showDateDeparture && (
            <Box mt={2} mb={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    onChange={(date) => {
                      if (date && date.isValid()) {
                        if (date.isBefore(moment(), "day")) {
                          return;
                        }
                        setSelectedDate(date);
                      } else {
                        setSelectedDate(null);
                      }
                    }}
                    name="departure_date"
                    value={selectedDate}
                    errors={{}}
                    label="Departure date"
                    required
                    disablePast
                    readOnlyInput
                    disabled={isUploading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Select
                    label={t("Departure") + " *"}
                    name="departure"
                    onChange={(e) => setSelectedDeparture(e.target.value)}
                    onBlur={() => { }}
                    value={selectedDeparture}
                    errors={{}}
                    options={sortOrder.map((o) => ({
                      label: t(o),
                      value: o,
                    }))}
                    disabled={isUploading}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            border="1px dashed #ccc"
            borderRadius={4}
            p={3}
            mt={2}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              accept=".xlsx, .xls"
              id="import-file"
              type="file"
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={isUploading}
            />

            {!file ? (
              <Box textAlign="center" py={3}>
                <DescriptionIcon
                  style={{ fontSize: 48, color: "#6F9CEB", marginBottom: 16 }}
                />
                <Typography variant="body1" gutterBottom>
                  {t("Drag and drop file here")}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t("or")}
                </Typography>
                <label htmlFor="import-file">
                  <Button
                    variant="contained"
                    color="primary"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={isUploading}
                  >
                    {t("Browse Files")}
                  </Button>
                </label>
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                  style={{ marginTop: 8 }}
                >
                  {t("Supported formats: .xlsx, .xls")}
                </Typography>
              </Box>
            ) : (
              <Box textAlign="center" py={2} width="100%">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                  position="relative"
                >
                  <DescriptionIcon style={{ color: "#6F9CEB", marginRight: 8 }} />
                  <Typography variant="body2">
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </Typography>
                  <IconButton
                    size="small"
                    style={{ position: "absolute", right: -10, top: -10 }}
                    onClick={handleRemoveFile}
                    disabled={isUploading}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <label htmlFor="import-file">
                  <Button
                    size="small"
                    color="primary"
                    component="span"
                    disabled={isUploading}
                  >
                    {t("Change File")}
                  </Button>
                </label>

                {localErrors.length > 0 && (
                  <Box
                    mt={2}
                    p={2}
                    bgcolor="rgba(255, 0, 0, 0.05)"
                    border="1px solid #ffcdd2"
                    borderRadius={4}
                    textAlign="left"
                  >
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      {t(errorMessage)}
                    </Typography>
                    {multilineImportErrors ? (
                      <Box mt={2}>
                        {localErrors.map((error, index) => (
                          <Box key={index} mb={0.5}>
                            <Typography variant="body2">
                              {typeof error === "string"
                                ? translateErrorMessage(error, t)
                                : typeof error === "object" &&
                                  error.row !== undefined &&
                                  Array.isArray(error.column)
                                  ? (
                                    <>
                                      {String(error.row) === "-" ? null : (
                                        <>
                                          {`${t("Row")} ${error.row}:`}
                                          <br />
                                        </>
                                      )}
                                      {error.column.map((c, i) => (
                                        <React.Fragment key={i}>
                                          {translateErrorMessage(String(c), t)}
                                          {i < error.column.length - 1 ? (
                                            <>
                                              <br />
                                              <br />
                                            </>
                                          ) : null}
                                        </React.Fragment>
                                      ))}
                                    </>
                                  )
                                  : typeof error === "object" && error.row !== undefined
                                    ? (String(error.row) === "-"
                                      ? translateErrorMessage(String(error.column), t)
                                      : `${t("Row")} ${error.row}: ${translateErrorMessage(String(error.column), t)}`)
                                    : String(error)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {localErrors.map((error, index) => (
                          <li key={index}>
                            <Typography variant="body2">
                              {typeof error === "string"
                                ? translateErrorMessage(error, t)
                                : typeof error === "object" &&
                                  error.row !== undefined &&
                                  Array.isArray(error.column)
                                  ? `${t("Row")} ${error.row}: ${error.column.map((c) => translateErrorMessage(String(c), t)).join("; ")}`
                                  : typeof error === "object" && error.row !== undefined
                                    ? `${t("Row")} ${error.row}: ${translateErrorMessage(String(error.column), t)}`
                                    : String(error)}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {isUploading && (
              <Box mt={2} width="100%">
                <Typography variant="body2" align="center" gutterBottom>
                  {uploadProgress < 100
                    ? t("Uploading... {{progress}}%", { progress: uploadProgress })
                    : t("Processing data...")}
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} color="primary" />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary" disabled={isUploading}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            disabled={
              !file ||
              isUploading ||
              (showDateDeparture
                ? !selectedDate || !selectedDeparture || (selectedDate && selectedDate.isBefore(moment(), "day"))
                : false)
            }
          >
            {t("Import")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileImportModal;